<template>
  <div class="dd">
    <div class="dd__container">
      <!-- Dropzone -->
      <div 
        v-bind="getRootProps()" 
        :class="['dropzone', { 'active-dropzone': isDragActive }]"
      >
        <input v-bind="getInputProps()" />
        <div class="dropzone-content">
          <CloudUploadIcon class="icon" />
          <p v-if="isDragActive">Отпустите файлы здесь</p>
          <p v-else>
            Перетащите файлы сюда или 
            <span class="link" @click.stop="open">выберите</span>
          </p>
          <small>Поддерживаются: JPG, PNG, SVG (макс. 10MB)</small>
        </div>
      </div>

      <!-- Список файлов -->
      <div v-if="files.length" class="file-list">
        <div v-for="(file, index) in files" :key="file.raw.name + index" class="file-item">
          <div class="file-info">
            <div class="file-preview-wrapper">
              <img 
                v-if="file.status === 'Успешно' && isImage(file.raw)" 
                :src="file.preview || (resImgPatch + file.response?.url)" 
                class="file-preview" 
              />
              <div v-else-if="file.status === 'Загрузка...'" class="loader"></div>
              <FileIcon v-else class="file-icon" />
            </div>
            <div>
              <div class="file-name">{{ file.raw.name }}</div>
              <div :class="['file-status', file.status === 'Успешно' ? 'success' : file.status === 'Ошибка' ? 'error' : 'uploading']">
                {{ file.status }}
              </div>
            </div>
          </div>

          <!-- Радио-подобный чекбокс -->
          <label class="checkbox-wrapper">
            <input 
              type="checkbox" 
              :checked="file.selected" 
              @change="selectFile(index)" 
            />
            <span class="custom-checkbox"></span>
          </label>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount } from "vue";
import { useDropzone } from "vue3-dropzone";
import axios from "axios";

const CloudUploadIcon = { template: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/></svg>' };
const FileIcon = { template: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>' };

export default {
  name: "Upload",
  components: { CloudUploadIcon, FileIcon },
  props: {
    initialFiles: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:selectedFiles","update:selected-files","selectedFiles","selected-files"],
  setup(props, { emit }) {
    const files = ref([]);
    const uploadUrl = "https://sitebypro-server.ru/static/upload";
    const resImgPatch = "https://sitebypro-server.ru/static";

    const isImage = (file) => file.type?.startsWith("image/");

    const resolveUrl = (maybeUrl) => {
      if (!maybeUrl) return null;
      try {
        const isAbsolute = /^(https?:)?\/\//i.test(maybeUrl);
        return isAbsolute ? maybeUrl : (resImgPatch + maybeUrl);
      } catch (e) {
        return resImgPatch + maybeUrl;
      }
    };

    const emitSelection = () => {
      const fileData = files.value.map(f => {
        const respUrl = f.response?.url ? resolveUrl(f.response.url) : null;
        const urlToSend = respUrl || f.preview;
        return {
          name: f.raw.name,
          url: urlToSend,
          selected: f.selected
        };
      });
      // отправляем сразу несколько вариантов события
      emit("update:selectedFiles", fileData);
      emit("update:selected-files", fileData);
      emit("selectedFiles", fileData);
      emit("selected-files", fileData);
    };

    const uploadFile = async (fileObj, index) => {
      if (fileObj.status === "Успешно" && fileObj.response?.url) return;

      const formData = new FormData();
      formData.append("file", fileObj.raw);

      try {
        files.value[index].status = "Загрузка...";
        const response = await axios.post(uploadUrl, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        const serverUrl = response.data?.url || response.data;
        const resolved = resolveUrl(serverUrl);

        files.value[index] = {
          ...files.value[index],
          status: "Успешно",
          response: { url: serverUrl },
          preview: resolved || files.value[index].preview
        };

        emitSelection();
      } catch (err) {
        files.value[index].status = "Ошибка";
        files.value[index].error = err?.message || String(err);
        emitSelection();
      }
    };

    const onDrop = (acceptedFiles) => {
      if (props.disabled) return; 
      const newFiles = acceptedFiles.map(file => ({
        raw: file,
        status: null,
        response: null,
        preview: isImage(file) ? URL.createObjectURL(file) : null,
        selected: false
      }));

      newFiles.forEach((fileObj, i) => {
        const index = files.value.length + i;
        files.value.push(fileObj);
        uploadFile(fileObj, index);
      });

      if (newFiles.length > 0) {
        files.value.forEach(f => f.selected = false);
        files.value[files.value.length - 1].selected = true;
        emitSelection();
      }
    };

    const initializeFiles = () => {
      props.initialFiles.forEach(file => {
        files.value.push({
          raw: { name: file.name || 'Файл', type: 'image/jpeg' },
          status: "Успешно",
          response: { url: file.url },
          preview: file.url,
          selected: false
        });
      });

      if (files.value.length > 0) {
        files.value.forEach(f => f.selected = false);
        files.value[files.value.length - 1].selected = true;
        emitSelection();
      }
    };

    const selectFile = (index) => {
      files.value.forEach((f, i) => f.selected = i === index);
      emitSelection();
    };

    const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
      onDrop,
      accept: "image/jpeg,image/png,application/pdf,svg",
      maxSize: 10 * 1024 * 1024,
      disabled: props.disabled,
    });

    onMounted(() => initializeFiles());

    onBeforeUnmount(() => {
      files.value.forEach(f => {
        if (f.preview && typeof f.preview === 'string' && f.preview.startsWith('blob:')) {
          try { URL.revokeObjectURL(f.preview); } catch(e) {}
        }
      });
    });

    return { files, getRootProps, getInputProps, open, isDragActive, isImage, resImgPatch, selectFile };
  }
};
</script>

<style scoped>
.dropzone-disabled {
  opacity: 0.6;
  pointer-events: none;
}
.file-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem;
  border-radius: 0.375rem;
  background-color: var(--color-0);
  color: var(--color-1);
  margin-bottom: 0.5rem;
  border: 0.1rem solid var(--color-1);
}
.file-info { display: flex; align-items: center; gap: 0.75rem; }

.checkbox-wrapper {
  display: flex;
  align-items: center;
  cursor: pointer;
  position: relative;
}
.checkbox-wrapper input[type="checkbox"] {
  opacity: 0;
  width: 0;
  height: 0;
}
.custom-checkbox {
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 50%;
  border: 2px solid var(--blue-1);
  display: inline-block;
  position: relative;
  transition: all 0.2s;
}
.checkbox-wrapper input[type="checkbox"]:checked + .custom-checkbox {
  background-color: var(--blue-1);
}
.checkbox-wrapper {
  display: flex;
  align-items: center;
  cursor: pointer;
  position: relative;
}

.checkbox-wrapper input[type="checkbox"] {
  opacity: 0;
  width: 0;
  height: 0;
}

.custom-checkbox {
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 50%;
  border: 2px solid var(--blue-1);
  display: inline-block;
  position: relative;
  transition: all 0.2s;
}

.custom-checkbox::after {
  content: "";
  position: absolute;
  display: none;
  left: 5.4px;
  top: 3px;
  width: 5px;
  height: 9px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.checkbox-wrapper input[type="checkbox"]:checked + .custom-checkbox {
  background-color: var(--blue-1);
}

.checkbox-wrapper input[type="checkbox"]:checked + .custom-checkbox::after {
  display: block;
}

.file-preview-wrapper {
  position: relative;
  width: 3.125rem;
  height: 3.125rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.file-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 0.5rem;
}

.loader {
  width: 2rem;
  height: 2rem;
  border: 3px solid #f3f3f3;
  border-top: 3px solid var(--blue-1);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.file-status.success { color: #38a169; }
.file-status.error { color: #e53e3e; }
.file-status.uploading { color: var(--blue-1); }

.file-preview {
  width: 3.125rem; 
  height: 3.125rem; 
  object-fit: cover;
  border-radius: 0.5rem;
}

.file-status {
  font-size: 0.8rem;
  margin-top: 0.25rem;
}

.file-status.success {
  color: #38a169;
}

.file-status.error {
  color: #e53e3e;
}

.dd__container {
  margin: 0 auto;
  padding: 0rem;
  margin-top: 0rem;
  border-radius: 1rem;
}

.dropzone {
  border: 0.125rem dashed var(--grey-0); /* 2px -> 0.125rem */
  border-radius: 0.5rem; /* 8px -> 0.5rem */
  padding: 1rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
  background-color: var(--color-0);
}

.active-dropzone {
  background-color: #ebf8ff;
  border-color: #63b3ed;
}

.dropzone-content {
  color: var(--color-1);
}

.icon {
  width: 3rem; /* 48px -> 3rem */
  height: 3rem; /* 48px -> 3rem */
  margin-bottom: 1rem;
  color: #a0aec0;
}

.link {
  color: #4299e1;
  text-decoration: underline;
  cursor: pointer;
}

.file-list {
  margin-top: 1rem;
}

.file-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem;
  border-radius: 0.375rem;
  background-color: var(--color-0);
  margin-bottom: 0.5rem;
}

.file-info {
  display: flex;
  align-items: center;
  gap: 0.75rem; /* 12px -> 0.75rem */
}

.file-icon {
  width: 1.5rem; /* 24px -> 1.5rem */
  height: 1.5rem; /* 24px -> 1.5rem */
  color: #4a5568;
}

.file-name {
  font-weight: 500;
  color: var(--color-1);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  max-width: 150px;
}

.file-size {
  font-size: 0.75rem; /* 12px -> 0.75rem */
  color: var(--grey-0);
}

.delete-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem; 
  color: var(--color-text);
}

.delete-btn:hover {
  color: #c53030;
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1rem;
}

.btn {
  padding: 0.8rem 1rem;
  border-radius: 0.5rem; /* 4px -> 0.25rem */
  font-weight: 500;
  cursor: pointer;
  font-weight: bold;
  transition: background-color 0.2s;
}

.btn.primary {
  background-color: var(--blue-0);
  color: var(--color-text);
  border: none;
}

.btn.primary:hover {
  background-color: #3182ce;
}

.btn.secondary {
  background-color: var(--grey-1);
  color: var(--color-text);
  border: none;
}

.btn.secondary:hover {
  background-color: var(--grey-0);
}

.dd {
  width: 100%;
}
</style>